import { Link } from 'react-router-dom';
import { 
  Home, DollarSign, Bed, Bath, Square, 
  GraduationCap, TreePine, Car, MapPin, TrendingUp, Building, ChevronRight
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useNeighborhoodStats } from '@/hooks/useNeighborhoodStats';
import { formatPrice, formatNumber } from '@/lib/formatters';
import { BreadcrumbSchema } from '@/components/seo/StructuredData';
import { Helmet } from 'react-helmet-async';

interface NeighborhoodIntroProps {
  neighborhood: string;
  city: string;
}

// Map neighborhoods to their parent hub areas
const SOUTH_EDMONTON_COMMUNITIES = [
  'Tamarack', 'Laurel', 'The Orchards at Ellerslie', 'Pollard Meadows', 
  'Summerside', 'Ellerslie', 'Charlesworth', 'Walker', 'Allard', 'Cavanagh'
];

const SOUTHWEST_EDMONTON_COMMUNITIES = [
  'Windermere', 'Keswick', 'Glenridding Heights', 'Ambleside', 
  'The Hamptons', 'Callaghan', 'Chappelle', 'Leger', 'Heritage Valley'
];

const SOUTHEAST_EDMONTON_COMMUNITIES = [
  'Meadows', 'Wild Rose', 'Larkspur', 'Maple', 'Silver Berry',
  'Millwoods', 'Lakewood', 'Lee Ridge', 'Tipaskan', 'Minchau'
];

function getParentHub(neighborhood: string, city: string): { name: string; url: string } | null {
  if (city !== 'Edmonton') return null;
  
  if (SOUTH_EDMONTON_COMMUNITIES.includes(neighborhood)) {
    return { name: 'South Edmonton Homes for Sale', url: '/edmonton/south-edmonton-homes-for-sale' };
  }
  if (SOUTHWEST_EDMONTON_COMMUNITIES.includes(neighborhood)) {
    return { name: 'Southwest Edmonton Homes for Sale', url: '/edmonton/southwest-edmonton-homes-for-sale' };
  }
  if (SOUTHEAST_EDMONTON_COMMUNITIES.includes(neighborhood)) {
    return { name: 'Southeast Edmonton Homes for Sale', url: '/edmonton/southeast-edmonton-homes-for-sale' };
  }
  return null;
}

// Neighborhood content database - can be extended or moved to Supabase later
const NEIGHBORHOOD_CONTENT: Record<string, {
  description?: string;
  highlights?: string[];
  schools?: string[];
  parks?: string[];
  commuteInfo?: string;
  idealFor?: string[];
}> = {
  // Edmonton neighborhoods
  'Windermere': {
    description: 'Windermere is one of Edmonton\'s most sought-after communities, featuring luxury homes, scenic walking trails, and excellent access to the Windermere Golf & Country Club.',
    highlights: ['Luxury estate homes', 'Golf course community', 'River valley access'],
    schools: ['Esther Starkman School', 'Bishop David Motiuk School'],
    parks: ['Windermere Neighbourhood Park', 'Terwillegar Park nearby'],
    commuteInfo: '25-35 min to downtown via Terwillegar Dr or Anthony Henday',
    idealFor: ['Families', 'Golf enthusiasts', 'Nature lovers'],
  },
  'Summerside': {
    description: 'Summerside is a vibrant lakeside community in south Edmonton, centered around the beautiful Summerside Beach Lake with year-round activities.',
    highlights: ['Private beach lake', 'Active community', 'Family-friendly'],
    schools: ['Monsignor Fee Otterson School', 'Dr. Lila Fahlman School'],
    parks: ['Summerside Beach', 'Blackmud Creek Ravine'],
    commuteInfo: '20-30 min to downtown via Gateway Blvd or QEII',
    idealFor: ['Young families', 'Active lifestyles', 'Lake lovers'],
  },
  'Glenora': {
    description: 'Glenora is one of Edmonton\'s oldest and most prestigious neighbourhoods, known for its tree-lined streets, heritage homes, and proximity to the river valley.',
    highlights: ['Heritage character', 'Mature trees', 'Central location'],
    schools: ['Glenora School', 'St. Vincent School'],
    parks: ['Coronation Park', 'Victoria Park'],
    commuteInfo: '10-15 min to downtown, walkable to many amenities',
    idealFor: ['Professionals', 'Heritage home enthusiasts', 'Urban lifestyle'],
  },
  'Tamarack': {
    description: 'Tamarack is a master-planned community in southeast Edmonton offering a mix of housing options, excellent amenities, and strong community spirit.',
    highlights: ['Master-planned community', 'Town centre amenities', 'Trail systems'],
    schools: ['Kameyosek School', 'Pollard Meadows School'],
    parks: ['Tamarack Common', 'Mill Creek Ravine access'],
    commuteInfo: '20-30 min to downtown via Whitemud Dr',
    idealFor: ['Young families', 'First-time buyers', 'Community-focused'],
  },
  'Laurel': {
    description: 'Laurel is a growing community in south Edmonton known for its family-friendly atmosphere, modern homes, and excellent connectivity to major roadways and shopping.',
    highlights: ['Modern homes', 'Growing community', 'Ellerslie Road access'],
    schools: ['Dr. Lila Fahlman School', 'Monsignor Fee Otterson School'],
    parks: ['Laurel Park', 'Blackmud Creek Ravine'],
    commuteInfo: '20-30 min to downtown via Gateway Blvd or Whitemud Dr',
    idealFor: ['Young families', 'First-time buyers', 'Commuters'],
  },
  'Allard': {
    description: 'Allard is a vibrant south Edmonton community featuring diverse housing options, scenic wetlands, and easy access to the Currents of Windermere shopping district.',
    highlights: ['Wetland views', 'Diverse housing', 'Shopping nearby'],
    schools: ['Monsignor William Irwin School', 'Bishop David Motiuk School'],
    parks: ['Allard Wetlands Park', 'Heritage Valley Park'],
    commuteInfo: '25-35 min to downtown via Terwillegar Dr or Anthony Henday',
    idealFor: ['Growing families', 'Nature enthusiasts', 'First-time buyers'],
  },
  'Keswick': {
    description: 'Keswick is a newer southwest Edmonton community offering modern homes, beautiful green spaces, and quick access to the Anthony Henday ring road.',
    highlights: ['New construction', 'Green spaces', 'Highway access'],
    schools: ['Kim Chicken School', 'Sister Annata Brockman School'],
    parks: ['Keswick Park', 'Henday Trail system'],
    commuteInfo: '25-35 min to downtown via Anthony Henday',
    idealFor: ['Young families', 'New home buyers', 'Commuters'],
  },
  'Meadows': {
    description: 'The Meadows is a large master-planned community in southeast Edmonton with excellent amenities, diverse housing, and a strong sense of community.',
    highlights: ['Master-planned', 'Recreation centre', 'Trail networks'],
    schools: ['The Meadows Community School', 'Dr. Anne Anderson School'],
    parks: ['Meadows Recreation Centre', 'Meadows Community Park'],
    commuteInfo: '20-30 min to downtown via Whitemud Dr',
    idealFor: ['Families', 'Active lifestyles', 'Community-focused'],
  },
  'Walker': {
    description: 'Walker is a family-friendly community in south Edmonton offering affordable homes, modern amenities, and convenient access to Ellerslie Road shopping.',
    highlights: ['Affordable homes', 'Family-oriented', 'Shopping access'],
    schools: ['Dr. Lila Fahlman School', 'Ellerslie Campus'],
    parks: ['Walker Park', 'Blackmud Creek'],
    commuteInfo: '20-30 min to downtown via Gateway Blvd',
    idealFor: ['First-time buyers', 'Young families', 'Budget-conscious'],
  },
  'Charlesworth': {
    description: 'Charlesworth is a well-established community in southeast Edmonton with mature landscaping, excellent schools, and a strong neighbourhood identity.',
    highlights: ['Established community', 'Mature landscaping', 'Family-friendly'],
    schools: ['Kameyosek School', 'J. Percy Page School'],
    parks: ['Charlesworth Park', 'Mill Creek Ravine'],
    commuteInfo: '20-25 min to downtown via Whitemud Dr',
    idealFor: ['Families', 'Long-term residents', 'School-focused'],
  },
  'Ambleside': {
    description: 'Ambleside is an upscale southwest Edmonton community featuring luxury homes, beautiful streetscapes, and proximity to the Currents of Windermere.',
    highlights: ['Luxury homes', 'Upscale finishes', 'Shopping nearby'],
    schools: ['Kim Chicken School', 'Sister Annata Brockman School'],
    parks: ['Ambleside Park', 'Heritage Valley trails'],
    commuteInfo: '25-35 min to downtown via Terwillegar Dr',
    idealFor: ['Move-up buyers', 'Professionals', 'Families'],
  },
  'Glenridding Heights': {
    description: 'Glenridding Heights offers stunning river valley views, modern homes, and a peaceful setting in southwest Edmonton with excellent amenities.',
    highlights: ['River valley views', 'Modern homes', 'Peaceful setting'],
    schools: ['Dr. Margaret-Ann Armour School', 'Sister Annata Brockman School'],
    parks: ['Glenridding Ravine', 'Heritage Valley Park'],
    commuteInfo: '30-40 min to downtown via Anthony Henday',
    idealFor: ['View seekers', 'Families', 'Nature lovers'],
  },
  'Chappelle': {
    description: 'Chappelle is a growing southwest Edmonton community with affordable new homes, great schools, and quick access to shopping at Heritage Valley.',
    highlights: ['New development', 'Affordable options', 'Growing amenities'],
    schools: ['Dr. Margaret-Ann Armour School', 'Chappelle School'],
    parks: ['Chappelle Gardens Park', 'Heritage Valley trails'],
    commuteInfo: '25-35 min to downtown via Anthony Henday',
    idealFor: ['First-time buyers', 'Young families', 'New home seekers'],
  },
  'Wild Rose': {
    description: 'Wild Rose is a well-established southeast Edmonton neighbourhood with mature trees, excellent schools, and a strong sense of community.',
    highlights: ['Established neighbourhood', 'Mature trees', 'Community spirit'],
    schools: ['Menisa School', 'J. Percy Page School'],
    parks: ['Wild Rose Park', 'Mill Creek Ravine'],
    commuteInfo: '20-25 min to downtown via Whitemud Dr',
    idealFor: ['Families', 'Long-term residents', 'Community-focused'],
  },
  'Silver Berry': {
    description: 'Silver Berry is a family-oriented southeast Edmonton community with affordable homes, good schools, and easy access to shopping and transit.',
    highlights: ['Affordable homes', 'Transit access', 'Family-friendly'],
    schools: ['Pollard Meadows School', 'Kate Chicken School'],
    parks: ['Silver Berry Park', 'Mill Creek Ravine'],
    commuteInfo: '20-25 min to downtown via Whitemud Dr',
    idealFor: ['First-time buyers', 'Families', 'Transit users'],
  },
  'The Hamptons': {
    description: 'The Hamptons is an upscale golf course community in northwest Edmonton, offering luxury homes and resort-style living with stunning course views.',
    highlights: ['Golf course living', 'Luxury homes', 'Gated sections'],
    schools: ['The Hamptons School', 'Winterburn School'],
    parks: ['The Hamptons Golf Course', 'Lewis Farms Park'],
    commuteInfo: '25-35 min to downtown via Anthony Henday',
    idealFor: ['Golf enthusiasts', 'Luxury seekers', 'Families'],
  },
  // Calgary neighborhoods
  'Aspen Woods': {
    description: 'Aspen Woods is one of Calgary\'s premier communities, featuring upscale homes, excellent schools, and convenient access to shopping at Aspen Landing.',
    highlights: ['Upscale shopping', 'Top-rated schools', 'Family-oriented'],
    schools: ['Webber Academy', 'St. Joan of Arc School'],
    parks: ['Edworthy Park nearby', 'Multiple playgrounds'],
    commuteInfo: '20-30 min to downtown via Bow Trail or Sarcee Trail',
    idealFor: ['Families', 'Professionals', 'Convenience seekers'],
  },
  'Bridgeland': {
    description: 'Bridgeland is a trendy inner-city neighbourhood just minutes from downtown Calgary, known for its eclectic shops, cafes, and stunning city views.',
    highlights: ['Inner-city location', 'Trendy restaurants', 'River access'],
    schools: ['Langevin School', 'Bridgeland-Riverside Community School'],
    parks: ['Tom Campbell\'s Hill', 'St. Patrick\'s Island'],
    commuteInfo: '5-10 min to downtown, walkable/bikeable',
    idealFor: ['Young professionals', 'Urban lifestyle', 'Foodies'],
  },
  'Altadore': {
    description: 'Altadore is a highly desirable inner-city community with a perfect blend of character homes and modern infills, close to Marda Loop shops.',
    highlights: ['Marda Loop shops', 'Character homes', 'Central location'],
    schools: ['Altadore School', 'St. Catherine School'],
    parks: ['River Park', 'Sandy Beach'],
    commuteInfo: '10-15 min to downtown via Crowchild Trail',
    idealFor: ['Families', 'Professionals', 'Active lifestyle'],
  },
  'Mahogany': {
    description: 'Mahogany is Calgary\'s largest lake community, offering residents access to two private beaches, a wetland area, and resort-style amenities.',
    highlights: ['Two private lakes', 'Beach club', 'Wetland trails'],
    schools: ['Mahogany School', 'Father Whelihan School'],
    parks: ['Mahogany Beach Club', 'Mahogany Wetlands'],
    commuteInfo: '25-35 min to downtown via Deerfoot Trail or Stoney Trail',
    idealFor: ['Families', 'Lake lovers', 'Active lifestyles'],
  },
  'Tuscany': {
    description: 'Tuscany is a well-established community in northwest Calgary with excellent schools, shopping, and quick access to the mountains via Highway 1.',
    highlights: ['Mountain access', 'Established amenities', 'Family community'],
    schools: ['Tuscany School', 'St. Basil School'],
    parks: ['Tuscany Club', 'Bowmont Park nearby'],
    commuteInfo: '25-35 min to downtown via Crowchild Trail or Stoney Trail',
    idealFor: ['Families', 'Mountain enthusiasts', 'Suburban lifestyle'],
  },
  'Downtown (Edmonton)': {
    description: 'Downtown Edmonton is the city\'s most dynamic and connected place to live—offering walkable convenience, an energetic lifestyle, and quick access to the river valley, major employers, and year-round entertainment. Whether you\'re a professional who wants a short commute, a first-time buyer looking for a condo, or an investor seeking rental demand near transit and amenities, Downtown Edmonton homes for sale provide a wide range of options with a true urban feel.',
    highlights: [
      'Walkable to restaurants, cafés, groceries, gyms, and services',
      'Entertainment hub with Ice District, Rogers Place, and nightlife',
      'LRT and major transit access',
      'River valley trails minutes away',
      'Mix of modern towers to boutique low-rises'
    ],
    schools: ['Oliver School', 'Centre High Campus', 'NAIT nearby'],
    parks: ['Louise McKinney Riverfront Park', 'Victoria Park', 'River Valley trails'],
    commuteInfo: 'Walk, bike, or LRT to downtown offices—minimal commute for central workers',
    idealFor: ['Young professionals', 'Condo buyers', 'Investors', 'Urban lifestyle seekers'],
  },
  'Oliver': {
    description: 'Oliver is one of Edmonton\'s most popular walkable neighbourhoods, offering condo-rich living with quick access to downtown, the river valley, and Jasper Avenue\'s shops and restaurants.',
    highlights: ['Walkable neighbourhood', 'Condo variety', 'River valley access', 'Central location'],
    schools: ['Oliver School', 'St. Joseph School'],
    parks: ['Victoria Park', 'Alexander Circle Park', 'River Valley trails'],
    commuteInfo: '5-10 min to downtown core, walkable to many amenities',
    idealFor: ['Young professionals', 'Condo seekers', 'Urban lifestyle'],
  },
  'Ice District': {
    description: 'Ice District is Edmonton\'s premier entertainment and residential hub, featuring modern high-rise living steps from Rogers Place, world-class dining, and vibrant nightlife.',
    highlights: ['Rogers Place arena', 'Modern high-rises', 'Premium dining', 'Entertainment central'],
    schools: ['Centre High Campus', 'Downtown schools'],
    parks: ['Louise McKinney Park', 'River Valley access'],
    commuteInfo: 'Heart of downtown—walk to work, events, and amenities',
    idealFor: ['Sports fans', 'Professionals', 'Entertainment seekers', 'Investors'],
  },
  'Brewery District': {
    description: 'Brewery District is a modern growth area on the edge of downtown Edmonton, featuring newer condos, trendy shops, and quick access to the core and river valley.',
    highlights: ['New development', 'Modern condos', 'Trendy shops and restaurants', 'Quick downtown access'],
    schools: ['Queen Alexandra School', 'Strathcona area schools'],
    parks: ['Strathcona Park', 'Mill Creek Ravine'],
    commuteInfo: '5-10 min to downtown, walkable to Whyte Avenue',
    idealFor: ['Young professionals', 'New condo buyers', 'Urban trendsetters'],
  },
  'Wîhkwêntôwin': {
    description: 'Wîhkwêntôwin (formerly Oliver) is one of Edmonton\'s most established and desirable central neighbourhoods, located just west of downtown. Known for walkable streets, mature trees, and a mix of high-rise and low-rise condo living, it offers quick access to Jasper Avenue, 124 Street, the Brewery District/Unity Square area, and the North Saskatchewan River Valley. The name is a Cree word meaning "circle of friends" (pronounced "wee-kwen-toe-win").',
    highlights: [
      'Walkable to cafés, groceries, restaurants, and daily essentials',
      'Central location between Downtown, 124 Street, and River Valley',
      'Strong condo selection at many price points—high-rise to boutique',
      'Lifestyle variety: quiet residential pockets + vibrant main corridors',
      'Parks and trails that make it feel livable—not just urban'
    ],
    schools: ['Oliver School', 'St. Joseph School', 'Centre High Campus'],
    parks: ['Victoria Park', 'Alexander Circle Park', 'River Valley trails', 'Coronation Park nearby'],
    commuteInfo: 'Easy transit access into Downtown; bike-friendly with River Valley trail connections; convenient driving routes for commuters who want central living',
    idealFor: ['Young professionals', 'Downsizers', 'Students', 'Investors', 'Condo seekers'],
  },
};

// Generate dynamic content based on stats when no curated content exists
function generateDynamicContent(neighborhood: string, city: string, stats: ReturnType<typeof useNeighborhoodStats>['data']) {
  if (!stats) return null;

  const topPropertyType = Object.entries(stats.propertyTypes)
    .sort((a, b) => b[1] - a[1])[0]?.[0] || 'homes';

  const priceLevel = stats.medianPrice < 400000 ? 'affordable' 
    : stats.medianPrice < 600000 ? 'mid-range'
    : stats.medianPrice < 900000 ? 'upper mid-range'
    : 'luxury';

  return {
    description: `${neighborhood} is a ${priceLevel} neighbourhood in ${city} with ${stats.totalListings} active listings. The area features primarily ${topPropertyType.toLowerCase()}s with a median price of ${formatPrice(stats.medianPrice)}.`,
    highlights: [
      `${stats.totalListings} active listings`,
      `Median price: ${formatPrice(stats.medianPrice)}`,
      `Avg ${stats.avgBedrooms} bed, ${stats.avgBathrooms} bath`,
    ],
    idealFor: stats.avgBedrooms >= 3 ? ['Families', 'Growing households'] : ['Young professionals', 'Couples'],
  };
}

export function NeighborhoodIntro({ neighborhood, city }: NeighborhoodIntroProps) {
  const { data: stats, isLoading } = useNeighborhoodStats(neighborhood, city);
  
  // Get curated content or generate dynamic content
  const curatedContent = NEIGHBORHOOD_CONTENT[neighborhood];
  const dynamicContent = !curatedContent ? generateDynamicContent(neighborhood, city, stats) : null;
  const content = curatedContent || dynamicContent;

  const canonicalUrl = `https://bookviewing.ca/search?neighborhood=${encodeURIComponent(neighborhood)}`;

  if (isLoading) {
    return (
      <Card className="mb-8">
        <CardContent className="p-6">
          <Skeleton className="h-8 w-64 mb-4" />
          <Skeleton className="h-20 w-full mb-4" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-16" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!stats || !content) return null;

  return (
    <>
      {/* SEO for neighborhood page */}
      <Helmet>
        <title>{neighborhood}, {city} Homes for Sale | Book Viewing</title>
        <meta 
          name="description" 
          content={`Explore ${stats.totalListings} homes for sale in ${neighborhood}, ${city}. Price range: ${stats.priceRange}. Book viewings online today!`} 
        />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      <BreadcrumbSchema
        items={[
          { name: "Home", url: "https://bookviewing.ca/" },
          { name: "Search", url: "https://bookviewing.ca/search" },
          { name: city, url: `https://bookviewing.ca/search?q=${encodeURIComponent(city)}` },
          { name: neighborhood, url: canonicalUrl }
        ]}
      />

      <Card className="mb-8 overflow-hidden">
        {/* Parent Hub Breadcrumb Link */}
        {(() => {
          const parentHub = getParentHub(neighborhood, city);
          if (parentHub) {
            return (
              <div className="bg-secondary/50 px-6 py-3 border-b">
                <Link 
                  to={parentHub.url}
                  className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  <Home className="w-4 h-4" />
                  <ChevronRight className="w-3 h-3" />
                  <span className="hover:underline">{parentHub.name}</span>
                  <ChevronRight className="w-3 h-3" />
                  <span className="font-medium text-foreground">{neighborhood}</span>
                </Link>
              </div>
            );
          }
          return null;
        })()}

        <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-6 border-b">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
                <MapPin className="w-4 h-4" />
                <span>{city}, Alberta</span>
              </div>
              <h1 className="font-display text-2xl md:text-3xl font-bold mb-2">
                Living in {neighborhood}
              </h1>
              {content.idealFor && (
                <div className="flex flex-wrap gap-2">
                  {content.idealFor.map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Price Range</p>
              <p className="font-semibold text-lg text-primary">{stats.priceRange}</p>
            </div>
          </div>
        </div>

        <CardContent className="p-6">
          {/* Description */}
          {content.description && (
            <p className="text-muted-foreground mb-6 leading-relaxed">
              {content.description}
            </p>
          )}

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-secondary/50 rounded-lg p-4 text-center">
              <Home className="w-5 h-5 mx-auto mb-2 text-primary" />
              <p className="text-2xl font-bold">{stats.totalListings}</p>
              <p className="text-xs text-muted-foreground">Active Listings</p>
            </div>
            <div className="bg-secondary/50 rounded-lg p-4 text-center">
              <DollarSign className="w-5 h-5 mx-auto mb-2 text-primary" />
              <p className="text-2xl font-bold">{formatPrice(stats.medianPrice).replace('$', '')}</p>
              <p className="text-xs text-muted-foreground">Median Price</p>
            </div>
            <div className="bg-secondary/50 rounded-lg p-4 text-center">
              <Bed className="w-5 h-5 mx-auto mb-2 text-primary" />
              <p className="text-2xl font-bold">{stats.avgBedrooms}</p>
              <p className="text-xs text-muted-foreground">Avg Bedrooms</p>
            </div>
            <div className="bg-secondary/50 rounded-lg p-4 text-center">
              <Square className="w-5 h-5 mx-auto mb-2 text-primary" />
              <p className="text-2xl font-bold">{formatNumber(stats.avgSqft)}</p>
              <p className="text-xs text-muted-foreground">Avg Sq Ft</p>
            </div>
          </div>

          {/* Property Type Breakdown */}
          {Object.keys(stats.propertyTypes).length > 1 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Building className="w-4 h-4" />
                Property Types Available
              </h3>
              <div className="flex flex-wrap gap-2">
                {Object.entries(stats.propertyTypes)
                  .sort((a, b) => b[1] - a[1])
                  .map(([type, count]) => (
                    <Badge key={type} variant="outline" className="text-xs">
                      {type}: {count}
                    </Badge>
                  ))}
              </div>
            </div>
          )}

          {/* Curated Content Sections */}
          {curatedContent && (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Schools */}
              {curatedContent.schools && curatedContent.schools.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-primary" />
                    Nearby Schools
                  </h3>
                  <ul className="space-y-1">
                    {curatedContent.schools.map((school) => (
                      <li key={school} className="text-sm text-muted-foreground flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary/50" />
                        {school}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Parks */}
              {curatedContent.parks && curatedContent.parks.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <TreePine className="w-4 h-4 text-primary" />
                    Parks & Recreation
                  </h3>
                  <ul className="space-y-1">
                    {curatedContent.parks.map((park) => (
                      <li key={park} className="text-sm text-muted-foreground flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary/50" />
                        {park}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Commute Info */}
              {curatedContent.commuteInfo && (
                <div>
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <Car className="w-4 h-4 text-primary" />
                    Commute Times
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {curatedContent.commuteInfo}
                  </p>
                </div>
              )}

              {/* Highlights */}
              {curatedContent.highlights && curatedContent.highlights.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-primary" />
                    Highlights
                  </h3>
                  <ul className="space-y-1">
                    {curatedContent.highlights.map((highlight) => (
                      <li key={highlight} className="text-sm text-muted-foreground flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary/50" />
                        {highlight}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
